import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type {
  WidgetConfig,
  WidgetState,
  SessionInfo,
  Message,
  PaymentIntentResponse,
  PaymentStatusResponse,
} from '../types';
import { ApiService } from '../services/api';
import { useWidget } from '../hooks/useWidget';
import { useChat } from '../hooks/useChat';
import { useVoice } from '../hooks/useVoice';
import { usePaymentStatus } from '../hooks/usePaymentStatus';


// Context value interface
interface WidgetContextValue {
  // Widget state
  state: WidgetState;
  initialize: (config: WidgetConfig) => Promise<void>;
  openWidget: () => void;
  closeWidget: () => void;
  toggleWidget: () => void;

  // Chat
  messages: Message[];
  isLoadingMessages: boolean;
  isSending: boolean;
  sendMessage: (content: string, wasVoiceInput?: boolean) => Promise<void>;
  acceptOffer: (paymentType: 'deposit' | 'full') => Promise<PaymentIntentResponse | null>;
  loadHistory: () => Promise<void>;

  // Voice
  isRecording: boolean;
  isProcessingVoice: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | null>;
  cancelRecording: () => void;
  isVoiceSupported: boolean;

  // Payment
  paymentStatus: PaymentStatusResponse | null;
  isPollingPayment: boolean;
  startPaymentPolling: () => void;
  stopPaymentPolling: () => void;

  // Stripe
  stripePublishableKey: string | null;

  // Errors
  error: string | null;
  resetError: () => void;
}

// Create context with undefined default
const WidgetContext = createContext<WidgetContextValue | undefined>(undefined);

// Provider props
interface WidgetProviderProps {
  children: React.ReactNode;
  config?: WidgetConfig;
}

export function WidgetProvider({ children, config }: WidgetProviderProps): JSX.Element {
  const [apiService, setApiService] = useState<ApiService | null>(null);

  // Widget hook
  const {
    state,
    initialize: initializeWidget,
    openWidget,
    closeWidget,
    toggleWidget,
    updateSession,
    resetError: resetWidgetError,
    error: widgetError,
  } = useWidget();

  // Track conversation state updates
  const handleConversationStart = useCallback((conversationId: string, sessionId: string) => {
    updateSession(conversationId, sessionId);
  }, [updateSession]);

  // Chat hook
  const {
    messages,
    isLoading: isLoadingMessages,
    isSending,
    sendMessage,
    acceptOffer,
    loadHistory,
    clearMessages,
    error: chatError,
    resetError: resetChatError,
  } = useChat({
    apiService,
    tenantSlug: state.config?.tenantSlug || '',
    conversationId: state.session?.conversationId || null,
    sessionId: state.session?.sessionId || null,
    onConversationStart: handleConversationStart,
  });

  // Voice hook with transcription callback
  const handleTranscription = useCallback(async (text: string) => {
    await sendMessage(text, true);
  }, [sendMessage]);

  const {
    isRecording,
    isProcessing: isProcessingVoice,
    startRecording,
    stopRecording,
    cancelRecording,
    error: voiceError,
    resetError: resetVoiceError,
    isSupported: isVoiceSupported,
  } = useVoice({
    apiService,
    onTranscription: handleTranscription,
  });

  // Payment status hook
  const handlePaymentComplete = useCallback((status: PaymentStatusResponse) => {
    // Could trigger a celebration animation or show confirmation
    console.log('Payment completed:', status);
  }, []);

  const handlePaymentFailed = useCallback((status: PaymentStatusResponse) => {
    console.log('Payment failed:', status);
  }, []);

  const handlePaymentTimeout = useCallback(() => {
    console.log('Payment polling timed out');
  }, []);

  const {
    status: paymentStatus,
    isPolling: isPollingPayment,
    startPolling: startPaymentPolling,
    stopPolling: stopPaymentPolling,
    error: paymentError,
    resetError: resetPaymentError,
  } = usePaymentStatus({
    apiService,
    conversationId: state.session?.conversationId || null,
    sessionId: state.session?.sessionId || null,
    onPaymentComplete: handlePaymentComplete,
    onPaymentFailed: handlePaymentFailed,
    onTimeout: handlePaymentTimeout,
  });

  // Initialize API service when config changes
  const initialize = useCallback(async (newConfig: WidgetConfig) => {
    const baseUrl = newConfig.apiBaseUrl ||
      (typeof window !== 'undefined' ? window.location.origin : '');

    const service = new ApiService(baseUrl, newConfig.tenantSlug);
    setApiService(service);

    await initializeWidget(newConfig);
  }, [initializeWidget]);

  // Auto-initialize if config is provided
  useEffect(() => {
    if (config && !state.isInitialized) {
      initialize(config);
    }
  }, [config, state.isInitialized, initialize]);

  // Load history when widget opens and there's an existing conversation
  useEffect(() => {
    if (state.isOpen && state.session?.conversationId && messages.length === 0) {
      loadHistory();
    }
  }, [state.isOpen, state.session?.conversationId, messages.length, loadHistory]);

  // Aggregate errors
  const error = widgetError || chatError || voiceError || paymentError;

  const resetError = useCallback(() => {
    resetWidgetError();
    resetChatError();
    resetVoiceError();
    resetPaymentError();
  }, [resetWidgetError, resetChatError, resetVoiceError, resetPaymentError]);

  // Derive Stripe publishable key from config
  const stripePublishableKey = state.config?.stripePublishableKey || null;

  // Memoize context value
  const contextValue = useMemo<WidgetContextValue>(() => ({
    // Widget
    state,
    initialize,
    openWidget,
    closeWidget,
    toggleWidget,

    // Chat
    messages,
    isLoadingMessages,
    isSending,
    sendMessage,
    acceptOffer,
    loadHistory,

    // Voice
    isRecording,
    isProcessingVoice,
    startRecording,
    stopRecording,
    cancelRecording,
    isVoiceSupported,

    // Payment
    paymentStatus,
    isPollingPayment,
    startPaymentPolling,
    stopPaymentPolling,

    // Stripe
    stripePublishableKey,

    // Errors
    error,
    resetError,
  }), [
    state,
    initialize,
    openWidget,
    closeWidget,
    toggleWidget,
    messages,
    isLoadingMessages,
    isSending,
    sendMessage,
    acceptOffer,
    loadHistory,
    isRecording,
    isProcessingVoice,
    startRecording,
    stopRecording,
    cancelRecording,
    isVoiceSupported,
    paymentStatus,
    isPollingPayment,
    startPaymentPolling,
    stopPaymentPolling,
    stripePublishableKey,
    error,
    resetError,
  ]);

  return (
    <WidgetContext.Provider value={contextValue}>
      {children}
    </WidgetContext.Provider>
  );
}

// Hook to use widget context
export function useWidgetContext(): WidgetContextValue {
  const context = useContext(WidgetContext);

  if (context === undefined) {
    throw new Error('useWidgetContext must be used within a WidgetProvider');
  }

  return context;
}

// Export context for advanced usage
export { WidgetContext };
export type { WidgetContextValue };
